import { Indicator, Root } from '@radix-ui/react-checkbox';
import { Check, Minus } from 'lucide-react';
import { forwardRef, type ComponentPropsWithoutRef, type ElementRef } from 'react';

import { cn } from '@/shared/utils/cn/cn.util';

export type CheckboxProps = ComponentPropsWithoutRef<typeof Root>;

/**
 * Tri-state by design, because the list kit's select-all header cell needs
 * "some, not all" to be distinguishable from "none" — Radix maps
 * `checked="indeterminate"` to `aria-checked="mixed"`, which is the part a
 * screen-reader user actually receives.
 */
export const Checkbox = forwardRef<ElementRef<typeof Root>, CheckboxProps>(function Checkbox(
  { className, checked, ...props },
  ref,
) {
  return (
    <Root
      ref={ref}
      checked={checked}
      className={cn(
        'peer inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border border-input bg-card transition-colors duration-fast ease-standard',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
        'disabled:cursor-not-allowed disabled:opacity-50',
        'data-[state=checked]:border-primary data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground',
        'data-[state=indeterminate]:border-primary data-[state=indeterminate]:bg-primary data-[state=indeterminate]:text-primary-foreground',
        className,
      )}
      {...props}
    >
      <Indicator className="inline-flex items-center justify-center text-current">
        {checked === 'indeterminate' ? (
          <Minus aria-hidden className="h-3 w-3" strokeWidth={3} />
        ) : (
          <Check aria-hidden className="h-3 w-3" strokeWidth={3} />
        )}
      </Indicator>
    </Root>
  );
});
